<template>
  <div class="d-events text-dark">
    <template v-if="type === 'swap'">
      <h2>{{ $t('vue2.edit_event.swap_headline') }}</h2>
      <p>{{ $t('vue2.edit_event.swap_desc') }}</p>
      <Loader v-if="isLoading" bg-color="#f6f9fc"></Loader>
      <template v-if="!isLoading">
        <Tabs :tabs="tabs" class="marg-1-top marg-1-bottom" :selected="selectedTab" @select="selectTab"></Tabs>
        <p class="text-explainer" v-if="!isLoading && filteredEventsVProps.length < 1">
          {{ $t('vue2.edit_event.swap_no_lessons') }}
        </p>
        <ul>
          <li
            v-for="event in filteredEventsVProps"
            :key="event.id"
            class="d-event"
            :style="{'backgroundColor': event.color}"
          >
            <div class="d-event__content">
              <b>{{ event.vTitle }},</b>
              <span>{{ event.vDateAndTime }}</span>
            </div>
            <button
              @click="eventSwap(event)"
              class="bttn"
              :class="{'bttn--loading': isSubmittingEventId === event.id }"
            >
              {{ $t('vue2.edit_event.select') }}
            </button>
          </li>
        </ul>
      </template>
    </template>

    <template v-if="type === 'replace'">
      <h2>{{ $t('vue2.edit_event.replace_headline') }}</h2>
      <p>{{ $t('vue2.edit_event.replace_desc') }}</p>
      <Loader v-if="isLoading" bg-color="#f6f9fc"></Loader>
      <p class="text-explainer marg-2-top" v-if="!isLoading && subjects.length < 1">
        {{ $t('vue2.edit_event.replace_no_lessons') }}
      </p>
      <ul v-if="!isLoading">
        <li
          v-for="subject in subjectsVProps"
          :key="subject.id"
          :style="{'backgroundColor': subject.color}"
          class="d-subject"
        >
          <div>{{ subject.vTitle }}</div>
          <button
            @click="replaceEvent(subject)"
            class="bttn"
            :class="{'bttn--loading': isSubmittingSubjectId === subject.id }"
          >
            {{ $t('vue2.edit_event.select') }}
          </button>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import Loader from '../common/Loader'
import moment from 'moment'
import Tabs from '../common/Tabs.vue'

const I18N = {
  da: {
    prevWeek: 'Ugen før',
    currentWeek: 'Samme uge',
    nextWeek: 'En uge frem',
    nextNextWeek: 'To uger frem'
  },
  nb: {
    prevWeek: 'Uken før',
    currentWeek: 'Samme uke',
    nextWeek: 'Neste uke',
    nextNextWeek: 'Om to uker'
  },
  en: {
    prevWeek: 'Previous week',
    currentWeek: 'Same week',
    nextWeek: 'Next week',
    nextNextWeek: 'In two weeks'
  }
}
export default {
  components: {Tabs, Loader},
  props: {
    type: {
      default: 'swap'
    },
    propEvent: {
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      isSubmittingSubjectId: null,
      isSubmittingEventId: null,
      events: [],
      subjects: [],
      selectedTab: 'currentWeek'
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    tabs () {
      return [
        {id: 'prevWeek', title: this.i18n.prevWeek},
        {id: 'currentWeek', title: this.i18n.currentWeek},
        {id: 'nextWeek', title: this.i18n.nextWeek},
        {id: 'nextNextWeek', title: this.i18n.nextNextWeek}
      ]
    },

    // Sortér uden at mutere original-array
    sortedEvents () {
      return this.events.slice().sort((a, b) => {
        return moment(a.event_time_start).unix() - moment(b.event_time_start).unix()
      })
    },

    // Beregn start/slut for valgt uge (ISO-uge: mandag–søndag)
    weekRange () {
      const base = moment(this.propEvent.start)
      switch (this.selectedTab) {
        case 'prevWeek':
          return {
            start: base.clone().startOf('isoWeek').subtract(1, 'week'),
            end: base.clone().endOf('isoWeek').subtract(1, 'week')
          }
        case 'nextWeek':
          return {
            start: base.clone().startOf('isoWeek').add(1, 'week'),
            end: base.clone().endOf('isoWeek').add(1, 'week')
          }
        case 'nextNextWeek':
          return {
            start: base.clone().startOf('isoWeek').add(2, 'weeks'),
            end: base.clone().endOf('isoWeek').add(2, 'weeks')
          }
        case 'currentWeek':
        default:
          return {
            start: base.clone().startOf('isoWeek'),
            end: base.clone().endOf('isoWeek')
          }
      }
    },

    // Filtrér events til den valgte uge (inklusiv kanter)
    filteredEvents () {
      const {start, end} = this.weekRange
      return this.sortedEvents.filter(ev => {
        const s = moment(ev.event_time_start)
        return s.isBetween(start, end, undefined, '[]')
      })
    },

    // VProps for viste events
    filteredEventsVProps () {
      return this.filteredEvents.map(event => ({
        ...event,
        vTitle: event.title,
        vTeachers: this.getTeachers(event),
        vDateAndTime: this.getDateAndTime(event)
      }))
    },

    subjectsVProps () {
      return this.subjects.map(subject => ({
        ...subject,
        vTitle: subject.name
      }))
    },

    schoolId () {
      return this.propEvent.school_id
    },

    eventId () {
      return this.propEvent.event_id
    }
  },
  watch: {
    type () {
      this.readPossibilities()
    }
  },
  methods: {
    getTeachers (event) {
      return 'test'
    },
    selectTab (tabId) {
      this.selectedTab = tabId
    },

    replaceEvent (subject) {
      this.isSubmittingSubjectId = subject.id
      docendo.eventReplace(this.schoolId, this.eventId, subject.id, success => {
        if (success.status && success.status === 'error') {
          eventbus.toast = {text: success.message, success: false}
          this.isSubmittingSubjectId = null
          return
        }
        eventbus.toast = {text: this.$t('vue2.edit_event.lesson_updated'), success: true}
        this.isSubmittingSubjectId = null
        this.$emit('update')
      }, failure => {
        console.log({failure})
        this.isSubmittingSubjectId = null
      })
    },

    eventSwap (event) {
      this.isSubmittingEventId = event.id
      docendo.eventSwap(this.schoolId, this.eventId, event.id, success => {
        if (success.status && success.status === 'error') {
          eventbus.toast = {text: success.message, success: false}
          this.isSubmittingEventId = null
          return
        }
        eventbus.toast = {text: this.$t('vue2.edit_event.lesson_updated'), success: true}
        this.isSubmittingEventId = null
        this.$emit('update')
        this.$store.dispatch('trackEvent', 'calendar_event_swaps_swapped')
      }, failure => {
        console.log({failure})
        this.isSubmittingEventId = null
      })
    },
    getDateAndTime (event) {
      if (!event.event_time_start) return ''
      const start = moment(event.event_time_start)
      const end = moment(event.event_time_end)
      if (this.locale === 'en') {
        return `${start.format('dddd, D MMM [at] HH:mm')} - ${end.format('HH:mm')}`
      } else {
        return `${start.format('dddd, D. MMM [kl.] HH:mm')} - ${end.format('HH:mm')}`
      }
    },
    readReplaceSubjects () {
      this.isLoading = true
      this.subjects = []
      docendo.eventListReplaceable(this.schoolId, this.eventId, success => {
        if (success.status && success.status === 'error') {
          eventbus.toast = {text: success.message, success: false}
          this.subjects = []
          this.isLoading = false
          return
        }
        this.subjects = success
        this.isLoading = false
      }, failure => {
        eventbus.toast = {text: 'Something went wrong', success: false}
        console.log('failure', failure)
        this.isLoading = false
      })
    },

    readSwapEvents () {
      this.isLoading = true
      this.events = []
      docendo.eventListSwapable(this.schoolId, this.eventId, success => {
        if (success.status && success.status === 'error') {
          eventbus.toast = {text: success.message, success: false}
          this.events = []
          this.isLoading = false
          return
        }
        this.events = success
        this.isLoading = false
      }, failure => {
        eventbus.toast = {text: 'Something went wrong', success: false}
        console.log({failure})
        this.isLoading = false
      })
    },

    readPossibilities () {
      if (this.type === 'swap') this.readSwapEvents()
      if (this.type === 'replace') this.readReplaceSubjects()
    }
  },
  mounted () {
    this.readPossibilities()
  }
}
</script>

<style scoped>

.d-subject {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  color: #ffffff !important;
  border-radius: 4px;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  text-align: left;
  margin: 0 0 0.5rem 0;
  min-width: auto;
  font-weight: bold;
  transition: all 0.25s;
  animation: fadeIn;
  cursor: pointer;
  line-height: 1em;
}

.d-event {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  color: #ffffff !important;
  border-radius: 4px;
  margin: 0 0 0.5rem 0;
  font-size: 12px;
}

.d-event__content,
.d-event .bttn {
  position: relative;
  z-index: 2;
}

.d-event:before {
  content: '';
  position: absolute;
  z-index: 1;
  left: 5px;
  top: 0;
  right: 0;
  bottom: 0;
  opacity: 0.15;
  background: #ffffff;
}

</style>



// WEBPACK FOOTER //
// EventSwapOrReplace.vue?bf8f42c8